import React, { FC } from "react";
import {
	CalendarEntryUpdate,
	useDropArea,
} from "Components/DropArea/DropAreaHelper";
import {
	CALENDAR_ENTRY_HEIGHT,
	TABLE_CELL_MARGIN_Y,
	CALENDAR_ENTRY_TYPE_HEIGHT,
	CalendarEntry,
} from "Components/Calendar/CalendarEntryHelper";
import { CSSProperties } from "@material-ui/styles";
import DropAreaIconView from "Components/DropArea/DropAreaIconView";
import { CellSize } from "Components/Calendar/CalendarHelper";
import DropAreaDroppableView from "Components/DropArea/DropAreaDroppableView";
import { Moment } from "moment";
import { useGetEnabledDaysDiff } from "Components/CompanySetting/CompanySettingHelper";
import { DropArea } from "Components/DropArea/types/DropArea";

interface Props {
	dropArea: DropArea;
	cellSize: CellSize;
	startOfCalendar: Moment;
	onClick: () => void;
	colorHex: string;
	acceptedDropTypes: string[];
	calendarEntryUpdate?: CalendarEntryUpdate;
	updateScheduleEntry?: (calendarEntry: CalendarEntry) => Promise<void>;
	addScheduleEntry?: (calendarEntry: CalendarEntry) => Promise<void>;
}

const CalendarMonthDropAreaView: FC<Props> = (props) => {
	const getEnabledDaysLength = useGetEnabledDaysDiff();
	const { isVisible, setIsHovered, isDroppingAllowed, draggedItem, drop } =
		useDropArea(
			props.acceptedDropTypes,
			props.calendarEntryUpdate,
			props.updateScheduleEntry,
			props.addScheduleEntry
		);

	const cellWidth = props.cellSize.month.dayWidth;
	const left = getLeft();

	function getDisplay() {
		if (props.dropArea.isPlaceholder && !draggedItem) {
			return "none";
		} else {
			return draggedItem && !isDroppingAllowed ? "none" : "flex";
		}
	}

	function getTop() {
		return TABLE_CELL_MARGIN_Y;
	}

	function getBottom() {
		if (draggedItem) {
			return TABLE_CELL_MARGIN_Y * 2 - 1;
		} else {
			return "unset";
		}
	}

	function getLeft() {
		return (
			getEnabledDaysLength(props.startOfCalendar, props.dropArea.start) *
			cellWidth
		);
	}

	function getWidth() {
		return (
			getEnabledDaysLength(
				props.dropArea.start,
				props.dropArea.end.clone().add(1, "day")
			) *
				cellWidth -
			1
		);
	}

	function getHeight() {
		if (draggedItem) {
			return "unset";
		} else {
			return props.dropArea.typeId
				? CALENDAR_ENTRY_TYPE_HEIGHT
				: CALENDAR_ENTRY_HEIGHT;
		}
	}

	function getStyle() {
		const style: CSSProperties = {
			zIndex: draggedItem ? 2 : 0,
			transform: `translate3d(${left + 1}px, ${getTop()}px, 0) scale(${
				isVisible ? 1 : 0.95
			})`,
			bottom: getBottom(),
			width: getWidth(),
			height: getHeight(),
			display: getDisplay(),
			opacity: isVisible ? 1 : 0,
		};

		return style;
	}

	return (
		<DropAreaDroppableView
			drop={drop}
			onClick={props.onClick}
			colorHex={draggedItem?.colorHex || props.colorHex}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			style={getStyle()}
		>
			{isVisible && (
				<DropAreaIconView
					dropArea={props.dropArea}
					draggedItem={draggedItem}
					calendarEntryUpdate={props.calendarEntryUpdate}
				/>
			)}
		</DropAreaDroppableView>
	);
};

export default CalendarMonthDropAreaView;
